/**
 * Saved-views + loans + version/branch/publish/sharing mutations that Report
 * Studio needs to run standalone, outside the Pipeline page. Uses the same
 * 'pipeline' demo-gate moduleId as PipelineIntelligence.tsx so a report built
 * here reflects the same book (live or demo) the Pipeline page itself shows —
 * this is a second, independent instance of that state, not a shared one, so
 * each page can be on a different saved view without fighting over one store.
 */
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { usePipelineLoans } from '@/hooks/useDemoDataAccess'
import { COMMAND_CENTER_LOANS } from '@/data/commandCenterLoans'
import { pipelineViewsService } from '@/services/pipelineViews'
import type { ColumnId, FilterState, SavedView, ViewAclUpdate } from './types'
import { dealScopeOf } from './helpers'
import { DEFAULT_COLUMNS, EMPTY_FILTERS } from './types'
import { DEFAULT_VIEW_SORT, savedViewSort } from './savedViewExport'

/**
 * @param initialViewId The report the Studio should open on. A report just created from the
 * Reporting page is one of these: it is a saved view, so it is listed here, but the list this
 * hook holds may have been fetched before it existed.
 */
export function useReportStudioWorkspace(initialViewId?: string | null) {
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS)
  // The deals the selected view reads: a view scoped to dead deals previews and
  // exports from the dead book, not an empty live one.
  const { loans, isLive } = usePipelineLoans('pipeline', COMMAND_CENTER_LOANS, dealScopeOf(filters))

  const viewsQuery = useQuery({
    queryKey: ['pipeline-views'],
    queryFn: () => pipelineViewsService.list(),
    staleTime: 30_000,
  })
  const savedViews = viewsQuery.data ?? []
  const [activeViewId, setActiveViewId] = useState<string>(initialViewId ?? 'active-pipeline')
  // The list row is enough to open the design. Versions & Sharing is not: an
  // admin's list JOIN used to show a different history than the owner for the
  // same id (call #16). GET /pipeline-views/{id} is the same payload for every
  // reader who can open it.
  const historyQuery = useQuery({
    queryKey: ['pipeline-view', activeViewId],
    queryFn: () => pipelineViewsService.get(activeViewId),
    enabled: Boolean(activeViewId) && activeViewId !== 'local-workspace',
    staleTime: 15_000,
  })
  // The report asked for is not in the list yet, and the list is on its way: wait for it rather
  // than opening on whichever report happens to be first.
  const awaitingInitialView =
    Boolean(initialViewId) &&
    activeViewId === initialViewId &&
    !savedViews.some((view) => view.id === initialViewId) &&
    viewsQuery.isFetching

  const localFallbackView = useMemo<SavedView>(
    () => ({
      id: 'local-workspace',
      name: 'Pipeline Report',
      description: 'Saved-view service unavailable — designs apply to this session only.',
      status: 'Draft',
      ownerEmail: 'local',
      version: 0,
      updatedAt: null,
      columns: DEFAULT_COLUMNS,
      filters: EMPTY_FILTERS,
      versions: [],
      isLocal: true,
    }),
    [],
  )
  const listedView =
    savedViews.find((view) => view.id === activeViewId) ??
    (awaitingInitialView ? undefined : savedViews[0]) ??
    localFallbackView
  const history = historyQuery.data
  const activeView =
    history && history.id === listedView.id
      ? {
          ...listedView,
          ...history,
          versions: history.versions,
        }
      : listedView
  const libraryViews = history
    ? savedViews.map((view) =>
        view.id === history.id ? { ...view, ...history, versions: history.versions } : view,
      )
    : savedViews

  const [visibleColumns, setVisibleColumns] = useState<ColumnId[]>(DEFAULT_COLUMNS)
  const [sort, setSort] = useState<{ id: ColumnId; direction: 'asc' | 'desc' }>({ ...DEFAULT_VIEW_SORT })
  const [toast, setToast] = useState<string | null>(null)

  // Hydrate the draft from the report the moment it becomes the active one — during the render
  // that makes it active, not in an effect after it. The Studio seeds its own draft from these
  // values when it mounts, so an effect would hand it the previous report's columns and filters
  // for exactly one render, and it never looks again.
  const [hydratedFor, setHydratedFor] = useState<string | null>(null)
  if (hydratedFor !== activeView.id) {
    setHydratedFor(activeView.id)
    // A report created with no filters stores `{}`; the Studio reads every key of the workspace
    // filters, so the keys it lacks come from the empty set rather than being undefined.
    setFilters({ ...EMPTY_FILTERS, ...(activeView.filters ?? {}) })
    setVisibleColumns(activeView.columns?.length ? activeView.columns : DEFAULT_COLUMNS)
    // The saved sort comes with the saved columns and filters: the Studio opens on this order,
    // and saving it back must not overwrite the one stored.
    setSort(savedViewSort(activeView))
  }

  useEffect(() => {
    if (awaitingInitialView) return
    if (savedViews.length && !savedViews.some((view) => view.id === activeViewId)) {
      setActiveViewId(savedViews[0].id)
    }
  }, [savedViews, activeViewId, awaitingInitialView])

  useEffect(() => {
    if (!toast) return
    const timer = window.setTimeout(() => setToast(null), 2800)
    return () => window.clearTimeout(timer)
  }, [toast])

  const assetClasses = useMemo(() => Array.from(new Set(loans.map((loan) => loan.type))).sort(), [loans])
  const assignees = useMemo(
    () => Array.from(new Set(loans.map((loan) => loan.assignee).filter(Boolean))).sort(),
    [loans],
  )
  const years = useMemo(
    () =>
      Array.from(new Set(loans.map((loan) => (loan.originated || '').slice(0, 4)).filter(Boolean)))
        .sort()
        .reverse(),
    [loans],
  )

  const applyDraft = (columns: ColumnId[], nextFilters: FilterState) => {
    setVisibleColumns(columns.length ? columns : DEFAULT_COLUMNS)
    setFilters(nextFilters)
    // A design carries its sort; a view that was never designed opens in the
    // grid's own order, not the previous view's.
    setSort(savedViewSort({ filters: nextFilters }))
  }

  const invalidateViews = async (fresh?: SavedView) => {
    // The pencil/Studio history is GET /pipeline-views/{id}. Invalidating only
    // the list left that cache in place, so a save or restore was overwritten
    // by the previous snapshots (Codex P1 on #533).
    if (fresh) queryClient.setQueryData(['pipeline-view', fresh.id], fresh)
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['pipeline-views'] }),
      queryClient.invalidateQueries({ queryKey: ['pipeline-view'] }),
    ])
  }

  const saveMutation = useMutation({
    mutationFn: async ({ view, note }: { view: SavedView; note: string }) => {
      if (view.name !== activeView?.name || view.description !== activeView?.description) {
        await pipelineViewsService.update(view.id, { name: view.name, description: view.description })
      }
      return pipelineViewsService.saveVersion(view.id, { note, columns: view.columns, filters: view.filters })
    },
    onSuccess: async (view) => {
      setActiveViewId(view.id)
      applyDraft(view.columns, view.filters)
      await invalidateViews(view)
      setToast(`Saved ${view.name} as v${view.version}`)
    },
  })

  const createMutation = useMutation({
    mutationFn: ({ name, columns, filters: nextFilters }: { name: string; columns: ColumnId[]; filters: FilterState }) =>
      pipelineViewsService.create({
        name,
        description: '',
        columns,
        filters: nextFilters,
        note: 'Created from Report Studio',
      }),
    onSuccess: async (view) => {
      setActiveViewId(view.id)
      applyDraft(view.columns, view.filters)
      await invalidateViews(view)
      setToast(`Created ${view.name}`)
    },
  })

  const branchMutation = useMutation({
    mutationFn: (view: SavedView) =>
      pipelineViewsService.branch(view.id, { name: view.name, description: view.description }),
    onSuccess: async (view) => {
      await invalidateViews(view)
      setActiveViewId(view.id)
      setToast(`Branched to ${view.name}`)
    },
  })

  const restoreMutation = useMutation({
    mutationFn: ({ view, version, note }: { view: SavedView; version: number; note: string }) =>
      pipelineViewsService.restoreVersion(view.id, version, { note }),
    onSuccess: async (view) => {
      setActiveViewId(view.id)
      applyDraft(view.columns, view.filters)
      await invalidateViews(view)
      setToast(`Restored ${view.name} to v${view.version}`)
    },
  })

  const publishMutation = useMutation({
    mutationFn: (view: SavedView) => pipelineViewsService.publish(view.id),
    onSuccess: async (view) => {
      await invalidateViews(view)
      setActiveViewId(view.id)
      setToast(`Published ${view.name}`)
    },
  })

  const aclMutation = useMutation({
    mutationFn: ({ view, acl }: { view: SavedView; acl: ViewAclUpdate }) => pipelineViewsService.updateAcl(view.id, acl),
    onSuccess: async (view) => {
      await invalidateViews(view)
      setActiveViewId(view.id)
      setToast(`Updated sharing for ${view.name}`)
    },
  })

  return {
    loans,
    // The rows are the illustrative sample, not the desk's book: exports say so.
    demoData: !isLive,
    view: activeView,
    allViews: libraryViews,
    viewsLoading: viewsQuery.isLoading,
    /** The Studio has nothing true to show yet: no list, or not the report it was asked to open. */
    opening: viewsQuery.isLoading || awaitingInitialView,
    viewsError: viewsQuery.isError,
    draftColumns: visibleColumns,
    draftFilters: filters,
    draftSort: sort,
    assetClasses,
    assignees,
    years,
    toast,
    saving:
      saveMutation.isPending ||
      createMutation.isPending ||
      branchMutation.isPending ||
      restoreMutation.isPending ||
      publishMutation.isPending ||
      aclMutation.isPending,
    onSelectView: (next: SavedView) => {
      setActiveViewId(next.id)
      applyDraft(next.columns, next.filters)
    },
    // Embedded on the Reporting page there's no page underneath to reveal by
    // "closing" — the studio's own Cancel button uses this to discard draft
    // edits back to the active view's last-saved columns/filters instead.
    onDiscardDraft: () => applyDraft(activeView.columns, activeView.filters),
    onSaveAsNew: async (name: string, columns: ColumnId[], nextFilters: FilterState) => {
      await createMutation.mutateAsync({ name, columns, filters: nextFilters })
    },
    onApply: (columns: ColumnId[], nextFilters: FilterState) => {
      applyDraft(columns, nextFilters)
      setToast('Applied to workspace — not saved as a version')
    },
    onSave: async (view: SavedView, message: string) => {
      await saveMutation.mutateAsync({ view, note: message })
    },
    onBranch: async (view: SavedView) => {
      await branchMutation.mutateAsync(view)
    },
    onRestore: async (view: SavedView, version: number, note: string) => {
      await restoreMutation.mutateAsync({ view, version, note })
    },
    onPublish: async (view: SavedView) => {
      await publishMutation.mutateAsync(view)
    },
    onUpdateAcl: async (view: SavedView, acl: ViewAclUpdate) => {
      await aclMutation.mutateAsync({ view, acl })
    },
    onInvalidated: invalidateViews,
  }
}
